import {
  createInstance,
  OptimizelyUserContext,
  UserAttributes,
  type Client,
} from '@optimizely/optimizely-sdk'
import {
  OptimizelyContextType,
  OptimizelyFeatureFlags,
  OptimizelyDecisionWithoutUserContext,
  defaultOptimizelyContext,
} from 'models/Optimizely'

/**
 * Singleton class for Optimizely SDK
 * This class is responsible for initializing the Optimizely SDK and providing methods
 * for getting feature flags, bucketing users into experiments, and tracking events.
 */
export class OptimizelySDK {
  private static instance: OptimizelySDK
  private client: Client | null = null
  private isInitializing = false
  private initPromise: Promise<Client | null> | null = null
  private constructor() {
    // Private constructor to prevent direct construction calls with 'new'
  }
  /**
   * Get the singleton instance of OptimizelySDK
   */
  public static getInstance(): OptimizelySDK {
    if (!OptimizelySDK.instance) {
      OptimizelySDK.instance = new OptimizelySDK()
    }

    return OptimizelySDK.instance
  }

  /**
   * Initialize the Optimizely SDK client
   * @returns Promise that resolves to the Optimizely client or null if initialization fails
   */
  public async initialize(): Promise<Client | null> {
    // If client is already initialized, return it
    if (this.client) {
      return this.client
    }

    // If initialization is in progress, return the existing promise
    if (this.isInitializing && this.initPromise) {
      return this.initPromise
    }

    // Start initialization
    this.isInitializing = true
    this.initPromise = this.initializeClient()

    try {
      this.client = await this.initPromise
      return this.client
    } catch (error) {
      console.error('Optimizely SDK initializeClient failed: ', error)
      return null
    } finally {
      this.isInitializing = false
      this.initPromise = null
    }
  }

  /**
   * Create a user context for the given user ID and attributes
   * @param userId User ID to create context for
   * @param userAttributes Optional user attributes
   * @returns UserContext or null if client is not initialized
   */
  public async createUserContext(
    userId: string,
    userAttributes?: UserAttributes,
  ): Promise<OptimizelyUserContext | null> {
    // TODO: cache user contexts
    const client = await this.initialize()

    if (!client) {
      return null
    }

    // Create a new user context directly from the client
    return client.createUserContext(userId, userAttributes)
  }

  /**
   * Helper method to process decisions for a specific type (feature flags or experiments)
   * @param userContext The Optimizely user context
   * @param keys The keys to get decisions for
   * @param defaultValues The default values to use if no decision is found
   * @returns The processed decisions
   */
  private processDecisions<T extends string>(
    userContext: OptimizelyUserContext | null,
    keys: T[],
    defaultValues: Record<T, OptimizelyDecisionWithoutUserContext>,
  ): Record<T, OptimizelyDecisionWithoutUserContext> {
    const result = { ...defaultValues }

    if (!userContext) {
      return result
    }

    const decisions = userContext.decideForKeys(keys)

    for (const [key, decision] of Object.entries(decisions)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { userContext, ...decisionWithoutUserContext } = decision
      result[key as T] = decisionWithoutUserContext
    }

    return result
  }

  /**
   * Get all decisions (feature flags and experiments) for a user
   * @param userId User ID to get decisions for
   * @param userAttributes Optional user attributes
   * @returns Promise that resolves to an OptimizelyContextType object
   */
  public async getDecisions(
    userId: string,
    userAttributes?: UserAttributes,
  ): Promise<OptimizelyContextType> {
    try {
      const userContext = await this.createUserContext(userId, userAttributes)

      return {
        featureFlags: this.processDecisions(userContext, Object.values(OptimizelyFeatureFlags), {
          ...defaultOptimizelyContext.featureFlags,
        }),
      }
    } catch (e) {
      console.error('Error getting Optimizely decisions:', e)
      return defaultOptimizelyContext
    }
  }

  private async initializeClient(): Promise<Client | null> {
    try {
      const sdkKey = process.env.OPTIMIZELY_NPO_FRONTEND_SDK_KEY

      if (!sdkKey) {
        console.error('Optimizely SDK key is not defined')
        return null
      }

      const client = createInstance({ sdkKey })

      if (!client) {
        console.error('Failed to create Optimizely client instance')
        return null
      }

      // Wait for the datafile to be ready
      await client.onReady({ timeout: 5000 })
      return client
    } catch (error) {
      console.error('Optimizely SDK failed to initialize:', error)
      return null
    }
  }
}

// Export a default instance for convenience
const instance = OptimizelySDK.getInstance()
export default instance
